// @ts-check
import React, { Component } from 'react';
import { Page, Row, Widget } from 'shared/containers/portal_layout';
import buildActions from './actions';
import LoadingContent from 'shared/components/LoadingContent';
import { newRule } from './utils';
import FlashMessageActions from 'shared/actions/FlashMessageActions';
import Rule from './Rule';
import { Rule as RuleType, RuleFormValues, RuleThenAction, RulableField } from './types'; // eslint-disable-line no-unused-vars

/** @typedef {{
  apiPrefix: string,
  useDisplayName?: boolean,
}} EditPropsType */

/** @typedef {{
  displayName?: string,
  fetched: boolean,
  availableActions: RuleThenAction[],
  rulableFields: RulableField[],
  rules: RuleType[],
}} EditStateType */

// Props:
// apiPrefix: The partial API path to a ruleset object
//   (.e.g. /agent_portal/api/provider/UUID/ruleset,
//          /admin_portal/api/organization/UUID/branded_ruleset)

/** @augments { Component<EditPropsType, EditStateType> } */
class Edit extends Component {
  constructor(props) {
    super(props);
    this.state = {
      displayName: null,
      fetched: false,
      availableActions: null,
      rulableFields: null,
      rules: null,
    };
    this.actions = buildActions(this.props.apiPrefix);
  }

  componentDidMount() {
    this.actions.fetchData().then(data => {
      this.setState({
        displayName: data.displayName,
        fetched: true,
        availableActions: data.availableActions,
        rulableFields: data.rulableFields,
        rules: data.rules,
      });
    });
  }

  addRule = () =>
    this.setState(state => ({
      rules: [...state.rules, newRule()],
    }));

  /**
   * @param {string} ruleId,
   * @param {RuleFormValues} formValues
   */
  createRule = (ruleId, formValues) =>
    this.actions.createRule(formValues).then(createdRule => {
      FlashMessageActions.flashSuccess({ message: 'Rule created!' });
      // Mark this rule as persisted so that subsequent saves use update instead of create.
      this.setState(state => ({
        rules: state.rules.map(r => (r.id === ruleId ? createdRule : r)),
      }));
    });

  /**
   * @param {string} ruleId,
   * @param {RuleFormValues} formValues
   */
  updateRule = (ruleId, formValues) =>
    this.actions.updateRule(ruleId, formValues).then(_response => {
      FlashMessageActions.flashSuccess({ message: 'Rule saved!' });
      // Update the rule in state with the formValues we submitted.
      this.setState(state => ({
        rules: state.rules.map(r => (r.id === ruleId ? { ...r, ...formValues } : r)),
      }));
    });

  /**
   * @param {string} ruleId
   * @param {RuleFormValues} formValues
   */
  saveRule = (ruleId, formValues) => {
    const rule = this.state.rules.find(r => r.id === ruleId);
    if (rule.persisted) {
      return this.updateRule(rule.id, formValues);
    } else {
      return this.createRule(rule.id, formValues);
    }
  };

  /**
   * @param {string} ruleId
   */
  deleteRule = ruleId => {
    if (window.confirm('Are you sure you want to delete this rule?')) {
      const rule = this.state.rules.find(r => r.id === ruleId);
      const promise = rule.persisted ? this.actions.deleteRule(rule.id) : Promise.resolve();
      return promise.then(_response => {
        this.setState(state => ({
          rules: state.rules.filter(r => r.id !== rule.id),
        }));
        FlashMessageActions.flashSuccess({ message: 'Rule deleted' });
      });
    }
  };

  render() {
    if (!this.state.fetched) return <LoadingContent />;
    return (
      <PresentationComponent
        useDisplayName={this.props.useDisplayName}
        displayName={this.state.displayName}
        rules={this.state.rules}
        availableActions={this.state.availableActions}
        rulableFields={this.state.rulableFields}
        addRule={this.addRule}
        saveRule={this.saveRule}
        deleteRule={this.deleteRule}
      />
    );
  }
}

export default Edit;

const PresentationComponent = props => (
  <Page
    title={
      props.useDisplayName ? `Referral Ruleset for ${props.displayName}` : 'My Referral Ruleset'
    }
  >
    <Row className="ruleset">
      <Widget
        title="Rule Builder"
        extraTitle={
          <div className="buttons">
            <button type="button" onClick={props.addRule} className="btn btn-mini btn-primary">
              <i className="icon icon-plus-sign icon-white" /> Add Rule
            </button>
          </div>
        }
        span={12}
      >
        {props.rules.length > 0 ? (
          <div>
            {props.rules.map(rule => (
              <Rule
                key={rule.id}
                rule={rule}
                availableActions={props.availableActions}
                rulableFields={props.rulableFields}
                saveRule={formValues => props.saveRule(rule.id, formValues)}
                deleteRule={() => props.deleteRule(rule.id)}
              />
            ))}
          </div>
        ) : (
          <div className="added-padding">
            <div className="well">
              <p>
                You haven't created any Referral Rules yet.{' '}
                <span className="fake-link" onClick={props.addRule}>
                  Click Here to add your first Referral Rule.
                </span>
              </p>
            </div>
          </div>
        )}
      </Widget>
    </Row>
  </Page>
);
